import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Search } from "lucide-react";

const PAGE_SIZE = 12;

export default function Traps() {
  const [traps, setTraps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  // Debounce the search input so we don't hammer the API on every keystroke.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(id);
  }, [q]);

  // Reset to page 0 whenever search changes.
  useEffect(() => { setPage(0); }, [debouncedQ]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (debouncedQ) params.q = debouncedQ;
    api.get("/opening-traps", { params })
      .then(({ data }) => {
        setTraps(data.traps);
        setTotal(data.total);
      })
      .finally(() => setLoading(false));
  }, [page, debouncedQ]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2">Opening traps</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8]">
            The classics — <span data-testid="traps-count">{total}</span> traps.
          </h1>
        </div>
        <div className="relative w-full sm:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756F67]" />
          <input
            data-testid="trap-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, ECO, or opening…"
            className="input-ink !pl-9"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-[#A8A196]">Loading…</div>
      ) : total === 0 ? (
        <div className="p-12 text-center text-[#A8A196]">No traps match "{debouncedQ}".</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="traps-grid">
            {traps.map((t) => (
              <Link
                key={t.id}
                to={`/traps/${t.id}`}
                data-testid={`trap-${t.id}`}
                className="card-panel p-5 hover:bg-[#25211B] hover:border-[#C6A15B]/40 transition-colors group"
              >
                <div className="flex items-start justify-between mb-2 gap-3">
                  <h3 className="font-serif text-xl text-[#EFE8D8] group-hover:text-[#C6A15B] transition-colors">
                    {t.name}
                  </h3>
                  {t.eco && (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#C6A15B] shrink-0 mt-1">
                      {t.eco}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#A8A196] leading-snug">{t.description}</p>
                <div className="mt-3 font-mono text-xs text-[#756F67]">{t.moves.length} moves →</div>
              </Link>
            ))}
          </div>

          {pageCount > 1 && (
            <div className="mt-8 flex items-center justify-center gap-3" data-testid="traps-pagination">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="btn-ghost text-sm disabled:opacity-40"
                data-testid="traps-prev"
              >
                ← Previous
              </button>
              <span className="font-mono text-xs text-[#A8A196]">
                Page {page + 1} of {pageCount}
              </span>
              <button
                onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
                disabled={page + 1 >= pageCount}
                className="btn-ghost text-sm disabled:opacity-40"
                data-testid="traps-next"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
